import { CsvExportModule } from '@ag-grid-community/csv-export';
import { AgGridReact } from '@ag-grid-community/react';
import { Grid } from '@mui/material';
import { WithStyles, withStyles } from '@mui/styles';
import {
    PageSelector, RecordsPerPageSelector, useAgGrid, useAgGridPagination
} from '@tassat/ag-grid';
import { ToolbarButton } from '@tassat/button';
import '@ag-grid-community/styles/ag-grid.css';
import '@ag-grid-community/styles/ag-theme-material.css';
import 'SharedComponents/agGrid/styles/agGridOverrides.css';
import {
    ActivityTypeSelectOptions,
    AgGridColumnDef,
    UserActivityLogTableOptions
} from '@tassat/common-definitions';
import { validateNumbersOnly } from '@tassat/forms/src/helpers/validationHelpers';
import { UserRecord } from '@tassat/fred-definitions';
import { Export as ExportIcon } from '@tassat/icon';
import React from 'react';

import { getCsvExportParams } from 'Lib/agGridHelper';
import { AG_GRID_SMALL_HEADER_HEIGHT, DEFAULT_ITEMS_PER_PAGE } from 'Lib/constant';
import Worker from 'Lib/worker/PrimusWorker';
import { formatActivitiesExport, formatActivitiesExportHeaders, getMappedColumn } from 'SharedComponents/activities/activitiesGridOptions';
import ExternalFilter from 'SharedComponents/agGrid/filters/externalFilter';
import ServerSort from 'SharedComponents/agGrid/serverSort';
import DateCellRender from 'SharedComponents/cellRenderComponent/datecellrender';
import ExternalFilterHeaderComponent from 'SharedComponents/cellRenderComponent/externalFilterHeaderComponent';
import { TextCellRender } from 'SharedComponents/cellRenderComponent/textCellrender';
import UserIdCellRenderer from 'SharedComponents/cellRenderComponent/userIdCellRenderer';
import { TableOptionsContext, tableOptionsReducer } from 'SharedComponents/contexts/TableOptionsContext';

interface UserActivityLogGridProps extends WithStyles<typeof muiStyles> {
    user: UserRecord;
}

const muiStyles = () => ({
    root: { position: 'relative' as const },
    exportButton: {
        position: 'absolute' as const,
        right: '1rem',
        bottom: '.5rem',
        zIndex: 10
    },
    exportIcon: {
        height: '16px',
        width: '24px'
    }
});

export function UserActivityLogGrid (props: UserActivityLogGridProps) {
    const {
        classes,
        user
    } = props;

    const tableOptionsInitialState: UserActivityLogTableOptions = React.useMemo(() => ({
        TableType: 'userActivities',
        PageNo: 1,
        RecordPerPage: DEFAULT_ITEMS_PER_PAGE,
        Sort: 'auditTime|DESC',
        DefaultSort: 'auditTime|DESC',
        SelectedDateFilter: '',
        DateCondition: '',
        SelectedFilter: 'none',
        TableFilters: [],
        FilterCondition: '',
        UserCondition: `userId|EQ|${user.UserId}`,
        Condition: '',
        DefaultState: true
    }), [user]);

    const [ activityData, setActivityData ] = React.useState<any[]>([]);
    const [ totalActivityCount, setTotalActivityCount ] = React.useState<number>(0);
    const [ gridApi, onGridReady ] = useAgGrid();
    const [ TableOptions, TableOptionsDispatch ] = React.useReducer<(TableOptions, { type, values }:
    { type: string; values: object }) => UserActivityLogTableOptions>(tableOptionsReducer, tableOptionsInitialState);
    const tableContextMemo = React.useMemo(() => {
        return {
            TableOptions,
            TableOptionsDispatch
        };
    }, [ TableOptions, TableOptionsDispatch ]);
    const [ setPageNum, setRecordsPerPage ] = useAgGridPagination(TableOptionsDispatch);

    React.useEffect(() => {
        if (gridApi) {
            gridApi.api.onFilterChanged();
        }
    }, [
        TableOptions.FilterCondition,
        TableOptions.Sort,
        TableOptions.DefaultState
    ]);

    React.useEffect(() => {
        fetchActivity();
    }, [
        TableOptions.PageNo,
        TableOptions.RecordPerPage,
        TableOptions.FilterCondition,
        TableOptions.Sort,
        TableOptions.DefaultState
    ]);

    const exportData = () => {
        if (gridApi) {
            gridApi.api.exportDataAsCsv(getCsvExportParams('user_activities', {
                formattingCallback: formatActivitiesExport,
                headerFormattingCallback: formatActivitiesExportHeaders
            }));
        }
    };

    const fetchActivity = async (pageNum?) => {
        const {
            PageNo,
            RecordPerPage,
            Sort,
            UserCondition,
            FilterCondition
        } = TableOptions;

        const res = await Worker.getInstance()
            .getFirmActivity({
                Condition: `${UserCondition}:${FilterCondition}`,
                Sort,
                PageNo: pageNum || PageNo,
                RecordPerPage
            });

        setTotalActivityCount(res.data.TotalCount);
        setActivityData(res.data?.activities || []);

        return res;
    };

    const userActivityLogColumnDefs: AgGridColumnDef[] = React.useMemo(() => ([
        {
            colId: 'AuditDateTime',
            headerName: 'Date / Time',
            field: 'AuditDateTime',
            cellRenderer: 'dateCellRender',
            minWidth: 140,
            headerComponent: 'externalFilterHeader',
            headerComponentParams: { getMappedColumn },
            filter: 'serverSort',
            filterParams: {
                getMappedColumn,
                defaultSortField: true,
                defaultSortDirection: 'DESC'
            },
            menuTabs: ['filterMenuTab'],
            flex: 1
        },
        {
            field: 'RequesterUsername',
            headerName: 'Requester Username',
            headerComponent: 'externalFilterHeader',
            minWidth: 140,
            headerComponentParams: { getMappedColumn },
            filter: 'externalFilter',
            filterParams: {
                getMappedColumn,
                inputType: 'text'
            },
            menuTabs: ['filterMenuTab'],
            flex: 1
        },
        {
            field: 'Username',
            headerName: 'Username',
            headerComponent: 'externalFilterHeader',
            minWidth: 140,
            headerComponentParams: { getMappedColumn },
            filter: 'externalFilter',
            filterParams: {
                getMappedColumn,
                inputType: 'text'
            },
            menuTabs: ['filterMenuTab'],
            flex: 1
        },
        {
            field: 'RequestId',
            headerName: 'Request ID',
            cellRenderer: 'textCell',
            headerComponent: 'externalFilterHeader',
            minWidth: 140,
            headerComponentParams: { getMappedColumn },
            filter: 'externalFilter',
            filterParams: {
                getMappedColumn,
                inputType: 'textEquals',
                validators: [validateNumbersOnly]
            },
            menuTabs: ['filterMenuTab'],
            flex: 1
        },
        //hidden type column for export
        {
            field: 'ActivityType',
            headerName: 'Type',
            hide: true
        },
        {
            field: 'Description',
            headerName: 'Type - Description',
            valueFormatter: ({ data }) => `${data.ActivityType} - ${data.Description}`,
            headerComponent: 'externalFilterHeader',
            headerComponentParams: { getMappedColumn },
            minWidth: 140,
            filter: 'externalFilter',
            filterParams: {
                getMappedColumn,
                inputType: 'select',
                selectOptions: ActivityTypeSelectOptions
            },
            menuTabs: ['filterMenuTab'],
            flex: 1
        }
    ]), [getMappedColumn]);

    return (
        <TableOptionsContext.Provider value={tableContextMemo}>
            <Grid
                className={classes.root}
                container
                direction='column'
                spacing={0}
                wrap='nowrap'
            >
                <ToolbarButton
                    className={classes.exportButton}
                    Icon={ExportIcon}
                    iconClassName={classes.exportIcon}
                    onClick={exportData}
                    text='Export'
                />
                <div
                    className='ag-theme-material'
                    id='userActivityLogGrid'
                    style={{ height: '420px' }}
                >
                    <AgGridReact
                        animateRows={false}
                        columnDefs={userActivityLogColumnDefs}
                        columnMenu='legacy'
                        components={{
                            externalFilterHeader: ExternalFilterHeaderComponent,
                            dateCellRender: DateCellRender,
                            userIdCell: UserIdCellRenderer,
                            textCell: TextCellRender,
                            serverSort: ServerSort,
                            externalFilter: ExternalFilter

                        }}
                        defaultColDef={{
                            resizable: true,
                            filter: false,
                            sortable: false
                        }}
                        enableCellTextSelection={true}
                        headerHeight={AG_GRID_SMALL_HEADER_HEIGHT}
                        modules={[CsvExportModule]}
                        onGridReady={onGridReady}
                        rowData={activityData}
                        suppressCellFocus={true}
                        suppressContextMenu={true}
                    />
                </div>
                {totalActivityCount > 0 &&
                    <Grid
                        container
                        direction='row'
                        spacing={0}
                        wrap='nowrap'
                    >
                        <RecordsPerPageSelector
                            currentRecordsPerPage={TableOptions.RecordPerPage}
                            setRecordsPerPage={setRecordsPerPage}
                        />
                        <PageSelector
                            changePageOnApplyOnly
                            currentPageNum={TableOptions.PageNo}
                            onPageChange={setPageNum}
                            recordsPerPage={TableOptions.RecordPerPage}
                            totalRows={totalActivityCount}
                        />
                    </Grid>}
            </Grid>
        </TableOptionsContext.Provider>
    );
}

export default withStyles(muiStyles)(UserActivityLogGrid);